import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";
import Pagination from "../../components/Pagination";
import { useNavigate } from "react-router-dom";
import { useButtonTableStyle } from "../../utils/useButtonTableStyle";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { createErpSocket } from "../../utils/socketIo";
import { getUserData } from "../const";
import { getSessionBranchId } from "../../utils/sessionBranch";
import {
  listGirviBanks,
  createGirviBank,
  updateGirviBank,
  isErpApiSuccess,
} from "../../erpClient";
import { formatIndianRupee } from "../../utils/formatLiveRate";
const ITEMS_PER_PAGE = 10;

function dealingTypeToUiLabel(apiValue) {
  const t = String(apiValue ?? "")
    .toLowerCase()
    .trim();
  if (t === "metal") return "Metal";
  if (t === "money" || t === "cash" || t === "mny") return "Money";
  return apiValue ? String(apiValue) : "";
}

function GirviBankDetails() {
  const navigate = useNavigate();
  const { buttonClass, tableClass, cardClass, buttonTableStyle } =
    useButtonTableStyle();

  const canBankDetails = hasPermission(PERMISSIONS.GIRVIBANKDETAILS);
  const canAddBank = hasPermission(PERMISSIONS.GIRVI_BANK_ADD);
  const canEditBank = hasPermission(PERMISSIONS.GIRVI_BANK_EDIT);
  const canBankLedger = hasPermission(PERMISSIONS.GIRVIBANKLEDGER);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Add Bank form states
  const [bankName, setBankName] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [accountStatus, setAccountStatus] = useState("active");
  const [socket, setSocket] = useState(null);

  const userData = getUserData();

  const fetchBankDetails = async () => {
    setIsLoading(true);
    try {
      const response = await listGirviBanks({});
      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.girvi_banks)
          ? response.data.girvi_banks
          : [];

      if (rows.length > 0) {
        const fetchedBankDetails = rows.map((bank) => {
          const typeLower = String(bank.account_type || "")
            .toLowerCase()
            .trim();
          const isMetal = typeLower === "metal";

          return {
            id: bank.id,
            bankName: bank.bank_name || "",
            accountHolder: bank.holder_name || "",
            accountType: dealingTypeToUiLabel(bank.account_type),
            ifscCode: bank.IFSC_code || bank.ifsc_code || "",
            accountNumber: bank.account_number || "",
            openingBalance: bank.opening_balance ?? "",
            balance:
              bank.available_balance !== null &&
                bank.available_balance !== undefined
                ? isMetal
                  ? `${bank.available_balance} gm`
                  : formatIndianRupee(bank.available_balance)
                : isMetal
                  ? "0 gm"
                  : formatIndianRupee(0),
            status:
              bank.status === "1" || bank.status === 1 ? "Active" : "Inactive",
            statusClass:
              bank.status === "1" || bank.status === 1
                ? "bg-success-subtle text-success"
                : "bg-danger-subtle text-danger",
            useForInvoice: bank.invoice === "1" || bank.invoice === 1,
            useForPOS: bank.pos === "1" || bank.pos === 1,
          };
        });

        fetchedBankDetails.sort((a, b) => b.id - a.id);
        setBankAccounts(fetchedBankDetails);
      } else {
        setBankAccounts([]);
        toast("No bank accounts found");
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      setBankAccounts([]);
      toast.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch bank details",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updateBankAccount = async (updatedAccount) => {
    try {
      const data = await updateGirviBank({
        id: updatedAccount.id,
        branch_id: getSessionBranchId(),
        bank_name: updatedAccount.bankName,
        holder_name: updatedAccount.accountHolder,
        IFSC_code: updatedAccount.ifscCode || "",
        account_number: updatedAccount.accountNumber || "",
        opening_balance: updatedAccount.openingBalance || "",
        status: updatedAccount.status === "Active" ? "1" : "0",
      });

      if (isErpApiSuccess(data) || data?.message === "radha" || data) {
        toast.success("Bank account updated successfully");
        fetchBankDetails();
      }
    } catch (error) {
      console.error("Error updating bank account details:", error);
      toast.error(
        error.response?.data?.message || "Failed to update bank account",
      );
    }
  };

  useEffect(() => {
    fetchBankDetails();
  }, []);

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      // console.log(
      //   "API URL changed in GirviBankDetails, refetching data...",
      //   event.detail,
      // );
      setCurrentPage(1);
      fetchBankDetails();
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  // Socket.IO listener for real-time updates
  useEffect(() => {
    const newSocket = createErpSocket();
    setSocket(newSocket);

    // Connection event logging
    newSocket.on("connect", () => {
      // console.log("GirviBankDetails: Connected to socket server", newSocket.id);
    });

    newSocket.on("disconnect", (reason) => {
      // console.log("GirviBankDetails: Disconnected from socket server:", reason);
    });

    newSocket.on("connect_error", (error) => {
      console.error("GirviBankDetails: Socket connection error:", error);
    });

    newSocket.on("girvi_bank_updated", (data) => {
      // console.log("Bank account updated via socket:", data);
      if (!data?.girvi_bank) return;

      setBankAccounts((prev) =>
        prev.map((account) =>
          account.id === data.girvi_bank.id
            ? {
              ...account,
              bankName: data.girvi_bank.bank_name || account.bankName,
              accountHolder:
                data.girvi_bank.holder_name || account.accountHolder,
              accountType:
                data.girvi_bank.account_type || account.accountType,
              ifscCode: data.girvi_bank.IFSC_code || account.ifscCode,
              accountNumber:
                data.girvi_bank.account_number || account.accountNumber,
              openingBalance:
                data.girvi_bank.opening_balance || account.openingBalance,
              balance:
                data.girvi_bank.available_balance !== null &&
                  data.girvi_bank.available_balance !== undefined
                  ? data.girvi_bank.account_type === "metal"
                    ? `${data.girvi_bank.available_balance} gm`
                    : formatIndianRupee(data.girvi_bank.available_balance)
                  : data.girvi_bank.account_type === "metal"
                    ? "0 gm"
                    : formatIndianRupee(0),
              status:
                data.girvi_bank.status === "1" || data.girvi_bank.status === 1
                  ? "Active"
                  : "Inactive",
              statusClass:
                data.girvi_bank.status === "1" || data.girvi_bank.status === 1
                  ? "bg-success-subtle text-success"
                  : "bg-danger-subtle text-danger",
            }
            : account,
        ),
      );
    });

    // Function to handle bank creation from socket
    const handleBankCreated = (data) => {
      if (!data?.girvi_bank) return;

      const newAccount = {
        id: data.girvi_bank.id,
        bankName: data.girvi_bank.bank_name || "",
        accountHolder: data.girvi_bank.holder_name || "",
        accountType: data.girvi_bank.account_type || "",
        ifscCode: data.girvi_bank.IFSC_code || "",
        accountNumber: data.girvi_bank.account_number || "",
        openingBalance: data.girvi_bank.opening_balance || "",
        balance:
          data.girvi_bank.available_balance !== null &&
            data.girvi_bank.available_balance !== undefined
            ? data.girvi_bank.account_type === "metal"
              ? `${data.girvi_bank.available_balance} gm`
              : formatIndianRupee(data.girvi_bank.available_balance)
            : data.girvi_bank.account_type === "metal"
              ? "0 gm"
              : formatIndianRupee(0),
        status:
          data.girvi_bank.status === "1" || data.girvi_bank.status === 1
            ? "Active"
            : "Inactive",
        statusClass:
          data.girvi_bank.status === "1" || data.girvi_bank.status === 1
            ? "bg-success-subtle text-success"
            : "bg-danger-subtle text-danger",
      };

      setBankAccounts((prev) => {
        // Check if account already exists (avoid duplicates)
        const exists = prev.some((account) => account.id === newAccount.id);
        if (exists) {
          // console.log("Account already exists, updating:", newAccount.id);
          return prev.map((account) =>
            account.id === newAccount.id ? newAccount : account,
          );
        }
        // console.log("Adding new account:", newAccount);
        // Add new account at the beginning (newest first)
        return [newAccount, ...prev].sort((a, b) => b.id - a.id);
      });
    };

    // Listen for both possible creation event names
    newSocket.on("girvi_bank_created", (data) => {
      // console.log(
      //   "Bank account created via socket (girvi_bank_created):",
      //   data
      // );
      handleBankCreated(data);
    });

    newSocket.on("girvi_bank", (data) => {
      // console.log("Bank account created via socket (girvi_bank):", data);
      handleBankCreated(data);
    });

    return () => {
      newSocket.off("girvi_bank_updated");
      newSocket.off("girvi_bank_created");
      newSocket.off("girvi_bank");
      newSocket.disconnect();
    };
  }, []);

  const handleEditClick = (account) => {
    if (!canEditBank) {
      toast.error("You do not have permission to edit bank details.");
      return;
    }
    setSelectedAccount({ ...account });
    setShowEditModal(true);
  };

  const handleCheckboxChange = (field) => {
    setSelectedAccount((prev) => {
      const updatedValue = !prev[field];

      return {
        ...prev,
        [field]: updatedValue,
      };
    });
  };

  const handleSaveChanges = () => {
    if (!canEditBank) {
      toast.error("Not authorized to edit bank");
      return;
    }
    // console.log("Saving changes with account:", selectedAccount);

    // Create a new update request to ensure proper handling
    const updatedData = {
      ...selectedAccount,
      useForInvoice: !!selectedAccount.useForInvoice, // Ensure boolean
      useForPOS: !!selectedAccount.useForPOS, // Ensure boolean
    };

    // console.log("Selected account before update:", updatedData);

    // Update local state immediately to reflect changes in UI
    setBankAccounts((prevAccounts) =>
      prevAccounts.map((account) => {
        if (account.id === selectedAccount.id) {
          return {
            ...selectedAccount,
            statusClass:
              selectedAccount.status === "Active"
                ? "bg-success-subtle text-success"
                : "bg-danger-subtle text-danger",
          };
        } else {
          // For invoice/POS flags, only uncheck others if this account is checked
          return {
            ...account,
            useForInvoice: selectedAccount.useForInvoice
              ? false
              : account.useForInvoice,
            useForPOS: selectedAccount.useForPOS ? false : account.useForPOS,
          };
        }
      }),
    );

    // Send update to API
    updateBankAccount(updatedData);
    setShowEditModal(false);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    try {
      if (!canAddBank) {
        toast.error("Not authorized to add bank");
        return;
      }

      const branchIdRaw = userData?.branch_id ?? userData?.branchId ?? 1;
      const branch_id =
        branchIdRaw !== "" && branchIdRaw != null
          ? Number(branchIdRaw) || 1
          : 1;

      // Match BankDetailsTable.jsx / createBankAccount payload shape
      const response = await createGirviBank({
        branch_id,
        bank_name: String(bankName || "").trim(),
        holder_name: String(accountHolderName || "").trim(),
        ifsc_code: String(ifscCode || "").trim(),
        account_number: String(accountNumber || "").trim(),
        opening_balance: Number(openingBalance) || 0,
      });

      if (
        response?.success === true ||
        response?.message === "radha" ||
        response?.data ||
        isErpApiSuccess(response)
      ) {
        toast.success("Bank account added successfully");
        fetchBankDetails();

        setShowAddModal(false);
        setBankName("");
        setAccountHolderName("");
        setIfscCode("");
        setAccountNumber("");
        setOpeningBalance("");
        setAccountStatus("active");
      } else {
        toast.error(response?.message || "Failed to add bank account");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        error.response?.data?.message || "Failed to add bank account",
      );
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const filteredAccounts = bankAccounts.filter((account) => {
    const matchesSearchQuery =
      account.bankName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      account.accountHolder.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesTypeFilter = typeFilter
      ? account.accountType === typeFilter
      : true;
    const matchesStatusFilter = statusFilter
      ? account.status === statusFilter
      : true;

    return matchesSearchQuery && matchesTypeFilter && matchesStatusFilter;
  });

  const totalPages = Math.ceil(filteredAccounts.length / itemsPerPage);
  const displayedAccounts = filteredAccounts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const handleItemsPerPageChange = (newSize) => {
    setItemsPerPage(newSize);
  };

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    } else if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
            }`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Bank Table</h4>
            <div className="d-flex gap-2">
              {canBankLedger && (
                <button
                  className={`btn btn-info ${buttonClass}`}
                  onClick={() => navigate("/girvibankledger")}
                >
                  Ledger
                </button>
              )}
              {canAddBank && (
                <button
                  className={`btn btn-success ${buttonClass}`}
                  onClick={() => setShowAddModal(true)}
                >
                  Add Bank
                </button>
              )}
            </div>
          </div>

          <div className="card-body p-4">
            {/* Search and Filter Section */}
            <div className="d-flex justify-content-between mb-3">
              <input
                type="text"
                className={`form-control ${buttonTableStyle === "square" ? "rounded-0" : ""
                  }`}
                style={{ maxWidth: "260px" }}
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
              <div className="d-flex gap-3">
                <select
                  className={`form-select ${buttonTableStyle === "square" ? "rounded-0" : ""
                    }`}
                  style={{ maxWidth: "200px" }}
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Types</option>
                  <option value="Money">Money</option>

                  <option value="Metal">Metal</option>
                </select>
                <select
                  className={`form-select ${buttonTableStyle === "square" ? "rounded-0" : ""
                    }`}
                  style={{ maxWidth: "200px" }}
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">All Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div
              className={`table-responsive mb-4 border ${cardClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
                }`}
            >
              <table
                className={`table text-nowrap mb-0 align-middle ${tableClass} ${buttonTableStyle === "square" ? "rounded-0" : ""
                  }`}
              >
                <thead className="text-dark fs-4">
                  <tr>
                    <th>Bank</th>
                    <th>Holder</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="5" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : (
                    displayedAccounts.map((account) => (
                      <tr key={account.id}>
                        <td>{account.bankName}</td>
                        <td>{account.accountHolder}</td>
                        <td>{account.balance}</td>
                        <td>
                          <span className={`badge ${account.statusClass}`}>
                            {account.status}
                          </span>
                        </td>
                        <td>
                          {canEditBank ? (
                            <button
                              className={`btn btn-primary btn-sm me-2 ${buttonClass}`}
                              onClick={() => handleEditClick(account)}
                            >
                              <Icon
                                icon="solar:pen-bold"
                                width="16"
                                height="16"
                              />
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={filteredAccounts.length}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>

      {/* Add Bank Account Modal */}
      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        centered
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Bank</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddSubmit}>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Bank Name</Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Bank Name"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  className={buttonClass}
                />
              </Col>
              <Col md={6}>
                <Form.Label>Account Holder Name</Form.Label>
                <Form.Control
                  type="text"
                  required
                  placeholder="Enter Account Holder Name"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                  className={buttonClass}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Account Number</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter Account Number"
                  value={accountNumber}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    setAccountNumber(numericValue);
                  }}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </Col>
              <Col md={6}>
                <Form.Label>IFSC Code</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter IFSC Code"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value)}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Label>Opening Balance</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter Opening Balance"
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(e.target.value)}
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </Col>
            </Row>

            {/* <Row className="mb-3">
              <Col md={6}>
                <Form.Label>
                  Account Status 
                </Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Active"
                    name="accountStatus"
                    value="active"
                    checked={accountStatus === "active"}
                    onChange={(e) => setAccountStatus(e.target.value)}
                  />
                  <Form.Check
                    type="radio"
                    label="Inactive"
                    name="accountStatus"
                    value="inactive"
                    checked={accountStatus === "inactive"}
                    onChange={(e) => setAccountStatus(e.target.value)}
                  />
                </div>
              </Col>
            </Row> */}

            <div className="d-flex flex-wrap gap-3 justify-content-end">
              <Button type="submit" variant="primary" className={buttonClass}>
                Submit
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Success Modal */}

      {/* Edit Bank Account Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        contentClassName={cardClass}
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Bank</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAccount && (
            <Form>
              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Bank Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedAccount.bankName}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        bankName: e.target.value,
                      })
                    }
                    className={buttonClass}
                  />
                </Form.Group>

                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Account Holder Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedAccount.accountHolder}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        accountHolder: e.target.value,
                      })
                    }
                    className={buttonClass}
                  />
                </Form.Group>
              </div>

              <div className="row">
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Account Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedAccount.accountNumber || ""}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        accountNumber: e.target.value,
                      })
                    }
                    className={buttonTableStyle === "square" ? "rounded-0" : ""}
                  />
                </Form.Group>

                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>IFSC Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={selectedAccount.ifscCode || ""}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        ifscCode: e.target.value,
                      })
                    }
                    className={buttonTableStyle === "square" ? "rounded-0" : ""}
                  />
                </Form.Group>
              </div>

              <div className="row">
                {/* Opening Balance */}
                <Form.Group className="mb-3 col-md-6">
                  <Form.Label>Opening Balance</Form.Label>
                  <Form.Control
                    type="number"
                    value={selectedAccount.openingBalance || ""}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        openingBalance: e.target.value,
                      })
                    }
                    className={buttonTableStyle === "square" ? "rounded-0" : ""}
                  />
                </Form.Group>
              </div>

              {/* Available Balance (Read-Only) */}
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Available Balance</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedAccount.balance}
                  readOnly
                  className={buttonTableStyle === "square" ? "rounded-0" : ""}
                />
              </Form.Group>

              {/* Account Status */}
              <Form.Group className="mb-3 col-md-6">
                <Form.Label>Account Status</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    label="Active"
                    name="accountStatus"
                    value="Active"
                    checked={selectedAccount.status === "Active"}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        status: e.target.value,
                      })
                    }
                  />
                  <Form.Check
                    type="radio"
                    label="Inactive"
                    name="accountStatus"
                    value="Inactive"
                    checked={selectedAccount.status === "Inactive"}
                    onChange={(e) =>
                      setSelectedAccount({
                        ...selectedAccount,
                        status: e.target.value,
                      })
                    }
                  />
                </div>
              </Form.Group>

              {/* Payment Preferences */}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={handleSaveChanges}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default GirviBankDetails;
